"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchApi } from "@local/utils/src/fetchApi";
import { rebuildUrlParams } from "@local/utils/src/rebuildUrlParams";
import type { Cart } from "@local/cart/src/cart";
import { useSearchParams } from "next/navigation";
import { getUpsellIdFallback } from "./upsellFallback";
import { persistUpsellChannel, upsellStorageKey } from "@local/tracking/src/upsellChannelStorage";
interface Props {
  channel: string;
  market: string;
}
/**
 * Custom hook for managing upsell functionality based on cart state
 * @param props - Configuration object
 * @param props.channel - The sales channel identifier
 * @param props.market - The market identifier
 * @returns Object containing upsellId and upsellUrl
 */
export const useUpsell = ({
  channel,
  market
}: Props) => {
  const searchParams = useSearchParams();
  const storageKey = upsellStorageKey(market, channel);

  // Start as null on both server and client to avoid SSR hydration mismatch.
  // Effect below hydrates from localStorage after mount — this setState in an
  // effect is intentional (external-store hydration), not derived state.
  const [upsellId, setUpsellId] = useState<string | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedId = window.localStorage.getItem(storageKey);
    if (storedId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUpsellId(storedId);
    }
  }, [storageKey]);

  // Tracks the `listingId:type` key the last /api/upsell/get lookup ran for —
  // not upsellId, which is the fetched upsell's own listingId and lives in a
  // different ID namespace. Comparing against upsellId meant the dedup check
  // never matched, so every cart-updated event (tip, tax, coupon, shipping,
  // gift card — not just adds) refetched even when the cart's most expensive
  // item hadn't changed. `type` (single/multi) is part of the key too — every
  // /api/upsell/get call also sends `typeId`, so a same-listingId cart that
  // flips single→multi still needs a refetch.
  const lastLookupKeyRef = useRef<string | null>(null);

  // Keys with a lookup currently in flight. `lastLookupKeyRef` is only stamped
  // once a usable upsell ID comes back, so a failed lookup retries on the next
  // cart event instead of poisoning the session — but without this second ref
  // a burst of `cart-updated` events would all fire before the first response
  // landed and stack concurrent /api/upsell/get calls. A Set (not a single
  // slot) so two different in-flight keys don't clear each other's marker.
  const inFlightKeysRef = useRef(new Set<string>());

  // Key derived from the most recent cart event. Lookups resolve out of order,
  // so a response whose key is no longer the latest describes a cart that no
  // longer exists — it must not overwrite upsellId or storage.
  const latestKeyRef = useRef<string | null>(null);
  const getCartMostExpensiveProductAndType = (event: Cart.CartEvent) => {
    try {
      const cart = event.detail.state;
      if (!cart) return [null, null];
      const sortedProducts = [...cart.items].filter(item => !item.parentItemId).sort((a, b) => b.price - a.price);
      const mostExpensiveProduct = sortedProducts[0];
      return [mostExpensiveProduct.listingId, sortedProducts.length > 1 ? "multi" : "single"];
    } catch (_) {
      // silently error
      return [null, null];
    }
  };
  const upsellHandler = useCallback(async (event_0: Event) => {
    try {
      const cartEvent = event_0 as Cart.CartEvent;

      // Get most expensive product and type from cart
      const [mostExpensiveProduct_0, type] = getCartMostExpensiveProductAndType(cartEvent);

      // If no most expensive product, return
      if (!mostExpensiveProduct_0 || !type) return;
      const lookupKey = `${mostExpensiveProduct_0}:${type}`;
      latestKeyRef.current = lookupKey;

      // Already resolved an upsell for this exact listing+type, or a lookup
      // for it is still in flight — either way, nothing to do.
      if (lookupKey === lastLookupKeyRef.current || inFlightKeysRef.current.has(lookupKey)) {
        return;
      }
      inFlightKeysRef.current.add(lookupKey);
      let foundUpsellId: string | null = null;

      // Get upsell for most expensive product
      try {
        const upsellRequest = await fetchApi<string>({
          endpoint: "/api/upsell/get",
          method: "POST",
          data: {
            channel: channel,
            typeId: type,
            listingId: mostExpensiveProduct_0,
            market
          }
        });
        foundUpsellId = upsellRequest || getUpsellIdFallback(market, channel);
      } finally {
        inFlightKeysRef.current.delete(lookupKey);
      }
      if (!foundUpsellId || foundUpsellId === "") return;

      // The cart moved on while this was in flight — drop the stale result
      // rather than pinning the user to an upsell for a product they no
      // longer have. Left unstamped so the key stays retryable.
      if (lookupKey !== latestKeyRef.current) return;

      // Stamped only now: a lookup that threw or came back empty must stay
      // retryable on the next cart event.
      lastLookupKeyRef.current = lookupKey;

      // The resolved ID is what drives upsellUrl — set it before the
      // best-effort persistence below, so a storage failure can't leave the
      // hook with a stamped key and no upsell.
      setUpsellId(foundUpsellId);

      // Save upsell ID to cart storage
      try {
        localStorage.setItem(storageKey, foundUpsellId);
        // Bridge the originating channel to the upsell page — the
        // /up/<id> route has no channel segment and TrackingLoader
        // would otherwise fall back to "store".
        persistUpsellChannel(foundUpsellId, channel);
      } catch (error_0) {
        console.error(error_0);
      }
    } catch (error) {
      console.error("Upsell handler error:", error);
    }
  }, [channel, market, storageKey]);

  // Listen to cart events
  useEffect(() => {
    window?.addEventListener?.("cart-updated", upsellHandler);
    return () => {
      window?.removeEventListener?.("cart-updated", upsellHandler);
    };
  }, [upsellHandler]);
  const upsellUrl = useMemo(() => {
    if (!upsellId) {
      return null;
    }
    return rebuildUrlParams(searchParams, `/${market}/up/${upsellId}`);
  }, [upsellId, market, searchParams]);

  // Build URL for upsell
  return {
    upsellId,
    upsellUrl
  };
};